import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import SearchClassroomsScreen from "../../../components/screens/Classrooms/SearchClassroomsScreen";
import { classrooms as classroomQuerys } from "@nualang/nualang-api-and-queries/Queries";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import config from "../../../config";
import ClassroomImage from "../../../img/classroom.svg";
import MembersImage from "../../../img/members.svg";
import { randomId } from "../../../utils/index";
import {
  getClassroomMembers,
  getClassroomMember,
  createClassroomMember,
  deleteClassroomMember,
} from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";
import { getClassrooms } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { classrooms as classroomHelpers, users as usersHelpers } from "../../../utils/helpers";
import useDebounce from "@nualang/nualang-ui-components/dist/hooks/useDebounce";
import { useApp, useSubscription, useClassroom } from "../../../hooks";

const { languages, forLanguages } = config;

function SearchClassrooms(props) {
  const {
    // eslint-disable-next-line react/prop-types
    authData: { username, attributes: authAttr },
    selectable,
    navigate,
    learnLang = "",
    forLang = "",
    handleSignIn,
    authenticated,
  } = props;
  const queryClient = useQueryClient();
  const {
    setNewClassroom,
    setIsJoinClassroomDialogOpen,
    memberLimit,
    setQuickCreateClassroomOpen,
  } = useClassroom();
  const { startLoading, stopLoading, openSnackbar } = useApp();
  const { subscription } = useSubscription();

  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const defaultLearnLang = learnLang || authAttr?.["custom:learnLang"] || "";
  const defaultForLang = forLang || authAttr?.["custom:forLang"] || "";
  const [filters, setFilters] = useState({
    learnLang: defaultLearnLang,
    forLang: defaultForLang,
    orderBy: "",
    limit: 12,
    visibility: "public",
    returnvalues: "",
    lastEvaluatedKey: "",
    lastEvaluatedClassroomId: "",
    lastEvaluatedCreatedAt: "",
    sort: "popular",
    search: "",
    verbose: false,
    page: 1,
  });

  const { orderBy } = filters;

  useEffect(() => {
    setFilters((prevFilters) =>
      prevFilters.learnLang === defaultLearnLang &&
      prevFilters.forLang === defaultForLang
        ? prevFilters
        : {
            ...prevFilters,
            learnLang: defaultLearnLang,
            forLang: defaultForLang,
            page: 1,
            lastEvaluatedKey: "",
            lastEvaluatedClassroomId: "",
            lastEvaluatedCreatedAt: "",
          },
    );
  }, [username, defaultLearnLang, defaultForLang]);

  useEffect(() => {
    setFilters((prevFilters) =>
      prevFilters.search === debouncedSearch
        ? prevFilters
        : { ...prevFilters, search: debouncedSearch },
    );
  }, [debouncedSearch]);
  const { ref, inView } = useInView();

  const {
    data = {},
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [...classroomQuerys.classroomKeys.list(filters), "search"],
    queryFn: async ({ pageParam }) => {
      try {
        const classrooms = await getClassrooms({
          ...filters,
          lastEvaluatedClassroomId: pageParam.classroomId
            ? pageParam.classroomId
            : "",
          page: pageParam.page || 1,
          lastEvaluatedCreatedAt: pageParam.createdAt
            ? pageParam.createdAt
            : "",
          lastEvaluatedKey: pageParam.classroomId ? pageParam.classroomId : "",
        });
        const formattedClassrooms = await classroomHelpers.formatClassrooms(
          classrooms.Items,
          false,
          props.authData,
        );
        return {
          ...classrooms,
          Items: formattedClassrooms,
        };
      } catch (error) {
        openSnackbar("search_error", "error");
        throw error;
      }
    },
    initialPageParam: {},
    getNextPageParam: (lastPage) => {
      if (
        lastPage.Items.length > 0 &&
        (lastPage.LastEvaluatedKey?.page ||
          lastPage.LastEvaluatedKey?.classroomId)
      ) {
        return lastPage.LastEvaluatedKey;
      }
      return undefined;
    },
  });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const allItems = useMemo(
    () => (data.pages || []).flatMap((page) => page.Items),
    [data],
  );

  // Mutations
  const joinClassroomMutation = classroomQuerys.useJoinClassroom(
    createClassroomMember,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (data, variables) => {
        openSnackbar("joined_classroom", "success");
        navigate(`/classrooms/${variables.classroomId}`);
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMembers(
            variables.classroomId,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMember(
            variables.classroomId,
            variables.username,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.list({
            memberId: username,
            verbose: false,
          }),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );
  const leaveClassroomMutation = classroomQuerys.useLeaveClassroom(
    deleteClassroomMember,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (data, variables) => {
        openSnackbar("left_classroom", "success");
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMembers(
            variables.classroomId,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMember(
            variables.classroomId,
            variables.username,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.list({
            memberId: username,
            verbose: false,
          }),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const handleFilterChange = (event) => {
    const { target } = event;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const { name } = target;

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
      page: 1,
      lastEvaluatedKey: "",
      lastEvaluatedClassroomId: "",
      lastEvaluatedCreatedAt: "",
    }));

    if (name === "search") {
      setSearch(value);
    }

    if (window.scrollY > 100) {
      window.scrollTo(0, 0);
    }
  };

  const orderKeys = ["popularity"];
  const orderOptions = orderKeys.map((key, idx) => (
    <MenuItem key={`orderclasssear_${idx}${randomId()}`} value={key}>
      {t(key)}
    </MenuItem>
  ));

  const handleJoinClassroom = (classroom, memberCount) => {
    if (authenticated) {
      setNewClassroom(classroom);
      if (memberCount >= memberLimit) {
        openSnackbar("classroom_member_limit_reached", "error");
      } else if (classroom.visibility === "private") {
        setIsJoinClassroomDialogOpen(true);
      } else {
        startLoading();
        joinClassroomMutation.mutate({
          classroomId: classroom.classroomId,
          username,
        });
      }
    } else {
      handleSignIn();
    }
  };

  const handleLeaveClassroom = async (classroomId) => {
    if (authenticated) {
      startLoading();
      leaveClassroomMutation.mutate({ classroomId, username });
    } else {
      handleSignIn();
    }
  };

  const handleViewClassroom = (classroomId) => {
    if (authenticated) {
      navigate(`/classrooms/${classroomId}`);
    } else {
      handleSignIn();
    }
  };

  const handleCreateClassroom = () => setQuickCreateClassroomOpen(true);

  const handleViewActivity = (memberId) => {
    navigate(`/activity/member/${memberId}`);
  };

  const getMemberDetails = (members) => usersHelpers.fetchMembersDetails(members);

  const handleViewUserProfile = (profileId) => {
    if (authenticated) {
      navigate(`/profiles/${profileId}`);
    } else {
      handleSignIn();
    }
  };

  return (
    <React.Fragment>
      <SearchClassroomsScreen
        t={t}
        isLoading={isLoading}
        placeholderImageUrl={ClassroomImage}
        memberPlaceholderImageUrl={MembersImage}
        classrooms={allItems || []}
        handleJoinClassroom={handleJoinClassroom}
        handleLeaveClassroom={handleLeaveClassroom}
        handleViewClassroom={handleViewClassroom}
        getMemberDetails={getMemberDetails}
        learnLang={filters.learnLang}
        forLang={filters.forLang}
        orderBy={orderBy}
        languages={languages}
        forLanguages={forLanguages}
        orderOptions={orderOptions}
        handleFilterChange={handleFilterChange}
        handleCreateClassroom={handleCreateClassroom}
        selectable={selectable}
        membersProps={{
          handleViewActivity,
        }}
        getClassroomMembers={getClassroomMembers}
        getClassroomMember={getClassroomMember}
        username={username}
        subscription={subscription}
        handleViewUserProfile={handleViewUserProfile}
        mainSiteUrl={config.SITE_URL}
        search={search}
        setSearch={setSearch}
      />
      <Box
        ref={ref}
        sx={{
          textAlign: "center",
          mt: 1
        }}>
        {isFetchingNextPage ? (
          <CircularProgress />
        ) : hasNextPage ? (
          <CircularProgress />
        ) : (
          ""
        )}
      </Box>
    </React.Fragment>
  );
}

SearchClassrooms.propTypes = {
  authData: PropTypes.object,
  navigate: PropTypes.func,
  selectable: PropTypes.bool,
};

export default SearchClassrooms;
